import React from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { Button, MediaMatch, useViewport } from '@lamimed/pantheon';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useGetProductBySlug } from 'hooks/useGetProductBySlug';
import { useSchedule } from 'hooks/useSchedule';
import { useScheduleStep } from 'hooks/useScheduleStep';

import { getQueryParamAsString } from 'utils/getQueryParamAsString';
import {
  whatsappMessage,
  whatsappNumber,
  whatsappUrl,
} from 'utils/whatsappUrl';

import { useProduct } from 'templates/Product/useProduct';
import { scheduleSteps } from 'templates/Schedule/shared/utils/scheduleSteps';

import { useCart } from '../CartDrawer/useCart';

import * as S from './styles';

export const ScheduleButton: React.FC = () => {
  const {
    query: { category, slug },
  } = useRouter();
  const { selectedSubproduct, scrollToSubproductAccordion } = useProduct();

  const { data } = useGetProductBySlug(getQueryParamAsString(slug));
  const { setIsCartOpen } = useCart();
  const { goToScheduleStep } = useScheduleStep();

  const { greaterThan } = useViewport();

  const { fillProductInfo, setSelectedSubproduct, steps } = useSchedule();

  const returnTrackingId = () => {
    switch (category) {
      case 'chat':
        return 'avvia-chat';
      case 'telemedicina':
        return 'prenota-telemedicina';
      default:
        return 'prenota';
    }
  };

  const handleScheduleClick = () => {
    datadogRum.addAction('bookProductFromScheduleButton', {
      cart: {
        product: data?.slug,
        type: data?.attendenceType,
        quantity: 1,
      },
    });

    if (data?.isTypology && !selectedSubproduct) {
      scrollToSubproductAccordion();

      return;
    }

    /**
     * TODO: Handle allowMultipleAppointments case for typologies.
     */
    if (data?.isTypology && selectedSubproduct) {
      fillProductInfo(selectedSubproduct.activityId, slug as string);

      setSelectedSubproduct(selectedSubproduct);

      const { index, path } = scheduleSteps.pickDate;

      goToScheduleStep({
        step: {
          index,
          path: `${path}?productId=${selectedSubproduct.activityId}&productSlug=${slug}`,
        },
        steps,
      });

      return;
    }

    if (typeof slug === 'string' && data) {
      fillProductInfo(data.activityId, slug);

      if (data.allowMultipleAppointments) {
        setIsCartOpen(true);

        return;
      }

      const { index, path } = scheduleSteps.pickDate;

      goToScheduleStep({
        step: {
          index,
          path: `${path}?productId=${data.activityId}&productSlug=${slug}`,
        },
        steps,
      });
    }
  };

  return (
    <S.Wrapper>
      <MediaMatch greaterThan="tablet">
        <S.Divider position="above" />
      </MediaMatch>

      {category === 'chat' ? (
        <Link
          href={whatsappUrl(whatsappNumber.default, whatsappMessage.default)}
          passHref
          legacyBehavior
        >
          <S.LinkProxy target="_blank">
            <Button
              variant="default"
              size="large"
              color="primary"
              data-tracking-id={returnTrackingId()}
              extended
            >
              Avvia Chat GRATIS
            </Button>
          </S.LinkProxy>
        </Link>
      ) : (
        <Button
          variant="default"
          size="large"
          color="primary"
          data-tracking-id={returnTrackingId()}
          extended={greaterThan('tablet') ? false : true}
          onClick={handleScheduleClick}
        >
          Prenota
        </Button>
      )}
    </S.Wrapper>
  );
};
